export default {
  title: 'Two-Factor Authentication',
  subtitle: 'Protect your account with an extra layer of security',
  
  status: {
    enabled: 'Enabled',
    disabled: 'Disabled',
    required: 'Required',
    optional: 'Optional',
  },

  setup: {
    title: 'Setup MFA',
    subtitle: 'Set up two-factor authentication',
    required: 'Two-factor authentication is required!',
    
    steps: {
      info: {
        title: 'Information',
        description: 'What you will need',
      },
      generate: {
        title: 'QR Code',
        description: 'Scan the QR code',
      },
      verify: {
        title: 'Verification',
        description: 'Enter the 6-digit code',
      },
      reauth: {
        title: 'Re-authentication',
        description: 'Security check required',
      },
    },

    info: {
      title: 'What you will need:',
      apps: [
        'Google Authenticator app',
        'Microsoft Authenticator app',
        'Or any TOTP compatible app',
      ],
      description: 'Two-factor authentication provides extra protection for your account. When signing in, you will need to enter a 6-digit code in addition to your password.',
      benefits: {
        title: 'Benefits:',
        items: [
          'Enhanced security',
          'Protection against unauthorized access',
          'Compliance with security requirements',
        ],
      },
    },

    qrCode: {
      title: 'Scan the QR code',
      description: 'Open your Authenticator app and scan this QR code:',
      manualKey: 'Manual setup key:',
      manualKeyDescription: 'Save this in a secure place in case you lose access!',
      copyKey: 'Copy key',
      keyCopied: 'Key copied to clipboard!',
    },

    verify: {
      title: 'Enter the 6-digit code',
      description: 'Enter the 6-digit code from your Authenticator app:',
      placeholder: '000000',
      codeRefreshes: 'The code refreshes every 30 seconds',
    },

    reauth: {
      title: 'Re-authentication required',
      description: 'For security reasons, please enter your password to set up two-factor authentication.',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
    },

    buttons: {
      cancel: 'Cancel',
      back: 'Back',
      continue: 'Continue',
      generateQR: 'Generate QR Code',
      enable: 'Enable',
      verifying: 'Verifying...',
      generating: 'Generating...',
    },

    messages: {
      success: 'Two-factor authentication successfully enabled!',
      successDescription: 'Your account is now protected. You will need to enter the 6-digit code on your next sign-in.',
      error: 'Error setting up MFA',
      invalidCode: 'Invalid verification code',
      wrongPassword: 'Wrong password',
      reauthRequired: 'For security reasons, you need to sign in again',
    },
  },

  verification: {
    title: 'Two-Factor Authentication',
    description: 'Enter the 6-digit code from your Authenticator app',
    codeLabel: 'Verification code',
    codePlaceholder: '000000',
    codeRefreshes: 'The code refreshes every 30 seconds',
    useBackupCode: 'Use backup code',
    trustDevice: 'Trust this device for 30 days',
    
    buttons: {
      verify: 'Sign In',
      verifying: 'Verifying...',
      cancel: 'Cancel',
    },

    messages: {
      success: 'Successfully logged in!',
      invalidCode: 'Invalid code. Please try again!',
      expiredCode: 'Code expired. Request a new one!',
      error: 'An error occurred during verification',
      quotaExceeded: '⏳ Too many attempts! Please wait 15-30 minutes and try again. Firebase daily quota has been exceeded, but it will reset soon.',
      tooManyAttempts: 'Too many failed attempts. Please wait a moment and try again.',
    },

    tip: {
      title: 'Tip:',
      description: 'Open the Authenticator app on your phone and enter the current 6-digit code.',
    },
  },

  backupCodes: {
    title: 'Backup Codes',
    description: 'These codes can be used once if you don\'t have access to your Authenticator app.',
    warning: 'Save these codes in a secure place!',
    
    generate: 'Generate new codes',
    regenerate: 'Regenerate codes',
    download: 'Download',
    print: 'Print',
    copy: 'Copy',
    
    status: {
      unused: 'Unused',
      used: 'Used',
      usedAt: 'Used at:',
    },

    remaining: '{count} codes remaining',
    
    messages: {
      generated: 'Backup codes successfully generated!',
      regenerated: 'Backup codes regenerated!',
      copied: 'Codes copied to clipboard!',
      downloadSuccess: 'Codes downloaded!',
      error: 'Error generating backup codes',
      confirmRegenerate: 'Are you sure you want to regenerate backup codes? Old codes will become invalid!',
    },
  },

  trustedDevices: {
    title: 'Trusted Devices',
    description: 'You won\'t need to enter the 6-digit code on these devices for 30 days.',
    
    current: 'Current device',
    browser: 'Browser',
    os: 'Operating System',
    ip: 'IP Address',
    addedAt: 'Added',
    expiresAt: 'Expires',
    lastUsed: 'Last used',
    
    remove: 'Remove',
    removeAll: 'Remove all',
    
    empty: 'No trusted devices',
    
    messages: {
      removed: 'Device removed!',
      removedAll: 'All devices removed!',
      error: 'Error removing device',
      confirmRemove: 'Are you sure you want to remove this device?',
      confirmRemoveAll: 'Are you sure you want to remove all trusted devices?',
    },
  },

  settings: {
    title: 'Two-Factor Authentication',
    description: 'Manage your two-factor authentication settings',
    
    status: {
      title: 'Status',
      enabled: 'Enabled',
      disabled: 'Disabled',
      enrolledAt: 'Enabled at:',
      lastUsed: 'Last used:',
      never: 'Never',
    },

    actions: {
      enable: 'Enable MFA',
      disable: 'Disable MFA',
      reconfigure: 'Reconfigure',
      viewBackupCodes: 'View backup codes',
      manageTrustedDevices: 'Manage trusted devices',
    },

    disable: {
      title: 'Disable MFA',
      description: 'Are you sure you want to disable two-factor authentication? This will reduce your account security.',
      passwordRequired: 'Enter your password to confirm:',
      confirm: 'Disable',
      cancel: 'Cancel',
      success: 'Two-factor authentication disabled',
      error: 'Error disabling MFA',
    },
  },

  admin: {
    title: 'MFA Administration',
    description: 'Organization-wide MFA settings and user management',
    
    policy: {
      title: 'MFA Policy',
      required: 'MFA required for all users',
      gracePeriod: 'Grace period (days)',
      allowBackupCodes: 'Allow backup codes',
      allowTrustedDevices: 'Allow trusted devices',
      trustedDeviceDays: 'Trusted device validity (days)',
      save: 'Save',
      saved: 'MFA policy saved!',
    },

    users: {
      title: 'User MFA Status',
      search: 'Search users...',
      name: 'Name',
      email: 'Email',
      status: 'MFA Status',
      enrolledAt: 'Enabled',
      actions: 'Actions',
      reset: 'Reset MFA',
      resetConfirm: 'Are you sure you want to reset {name}\'s MFA settings?',
      resetSuccess: 'MFA successfully reset!',
      resetError: 'Error resetting MFA',
    },

    stats: {
      title: 'Statistics',
      totalUsers: 'Total users',
      mfaEnabled: 'MFA enabled',
      mfaDisabled: 'MFA disabled',
      percentage: 'MFA usage',
    },
  },

  activity: {
    title: 'MFA Activity Log',
    description: 'Log of all MFA events',
    
    types: {
      setup: 'MFA enabled',
      verify: 'Sign in with MFA',
      disable: 'MFA disabled',
      backup_code_used: 'Backup code used',
      trusted_device_added: 'Trusted device added',
      trusted_device_removed: 'Trusted device removed',
    },

    columns: {
      timestamp: 'Timestamp',
      type: 'Event',
      status: 'Status',
      ip: 'IP Address',
      details: 'Details',
    },

    status: {
      success: 'Success',
      failed: 'Failed',
    },

    empty: 'No MFA activity',
  },
};
